import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  IconButton,
  Checkbox,
  Alert,
  Snackbar,
  Popover,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Divider,
  Tooltip,
} from '@mui/material';
import { Visibility, Close, AddCircleOutline, LocationOn, Phone, Email, CalendarToday, Edit, Lock, Delete, Add } from '@mui/icons-material';
import api from '../services/api';

interface Order {
  id: number;
  woo_order_id: number;
  order_number: string;
  status: string;
  collection_status: string;
  total: string;
  customer_first_name: string;
  customer_last_name: string;
  previous_orders_count?: number;
  loyalty_member?: number;
  delivery_postcode: string;
  shipping_collection_date: string;
  collection_dates?: string;
  service_type: string;
  product_names?: string;
  driver_name?: string;
  assigned_user_id?: number; // Legacy field from orders table
  assigned_driver_id?: number; // New field from collection_events
  // Additional fields for detailed view
  delivery_address_1?: string;
  delivery_address_2?: string;
  delivery_city?: string;
  delivery_country?: string;
  billing_address_1?: string;
  billing_address_2?: string;
  billing_city?: string;
  billing_postcode?: string;
  billing_country?: string;
  customer_email?: string;
  customer_phone?: string;
  customer_note?: string;
  items?: string;
  date_created?: string;
  bins_per_occasion?: number;
  bins_number_of_occasions?: number;
  bags_per_occasion?: number;
  bags_number_of_occasions?: number;
  large_bins_per_occasion?: number;
  large_bins_number_of_occasions?: number;
  beds_count?: number;
  mattress_count?: number;
  bins_first_date?: number;
  bags_first_date?: number;
  large_bins_first_date?: number;
  first_date_bin_colours?: string;
  assigned_at?: string;
  completed_at?: string;
  bin_colours?: string;
  bin_brown?: number;
  bin_green?: number;
  bin_blue?: number;
  bin_black?: number;
  bin_grey?: number;
  bin_pink?: number;
  total_collections?: number;
  completed_collections?: number;
  pending_collections?: number;
}

interface Driver {
  id: number;
  name: string;
  driver_code: string;
  status: string;
}

const OrderManagement: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedOrders, setSelectedOrders] = useState<number[]>([]);
  const [bulkAction, setBulkAction] = useState('');
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [datePopover, setDatePopover] = useState<{ anchorEl: HTMLElement | null; dates: string[] }>({ anchorEl: null, dates: [] });
  const [orderDetailsModal, setOrderDetailsModal] = useState<{ open: boolean; order: Order | null }>({ open: false, order: null });
  const [collectionEvents, setCollectionEvents] = useState<any[]>([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [cancelConfirmDialog, setCancelConfirmDialog] = useState<{ open: boolean; orderId: number | null }>({ open: false, orderId: null });
  const [revertConfirmDialog, setRevertConfirmDialog] = useState<{ open: boolean; orderId: number | null }>({ open: false, orderId: null });
  const [dateDialog, setDateDialog] = useState<{ open: boolean; mode: 'add' | 'edit'; eventId: number | null; currentDate: string }>({
    open: false,
    mode: 'add',
    eventId: null,
    currentDate: ''
  });
  const [selectedDate, setSelectedDate] = useState('');

  // Address and notes editing state
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [editAddressData, setEditAddressData] = useState({
    delivery_address_1: '',
    delivery_address_2: '',
    delivery_city: '',
    delivery_postcode: '',
    delivery_country: ''
  });
  const [savingAddress, setSavingAddress] = useState(false);
  const [adminNoteText, setAdminNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [editNoteText, setEditNoteText] = useState('');

  // Bin colours editing state
  const [isEditingBinColours, setIsEditingBinColours] = useState(false);
  const [editBinColours, setEditBinColours] = useState({
    bin_brown: 0,
    bin_green: 0,
    bin_blue: 0,
    bin_black: 0,
    bin_grey: 0,
    bin_pink: 0,
  });
  const [savingBinColours, setSavingBinColours] = useState(false);

  useEffect(() => {
    fetchOrders();
    fetchDrivers();
  }, [page, rowsPerPage, statusFilter, searchTerm]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
      });
      
      if (statusFilter) params.append('orderStatus', statusFilter);
      if (searchTerm) params.append('search', searchTerm);

      const response = await api.get(`/orders?${params}`);
      setOrders(response.data.orders);
      setTotalCount(response.data.pagination.total);
      setSelectedOrders([]); // Clear selection when fetching new data
    } catch (error) {
      console.error('Error fetching orders:', error);
      showNotification('Failed to fetch orders', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchDrivers = async () => {
    try {
      const response = await api.get('/users');
      setDrivers(response.data.filter((user: Driver) => user.status === 'active'));
    } catch (error) {
      console.error('Error fetching drivers:', error);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };


  const getOrderStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'processing':
        return 'info';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = orders.map(order => order.id);
      setSelectedOrders(newSelected);
    } else {
      setSelectedOrders([]);
    }
  };

  const handleSelectOrder = (orderId: number) => {
    const selectedIndex = selectedOrders.indexOf(orderId);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedOrders, orderId);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedOrders.slice(1));
    } else if (selectedIndex === selectedOrders.length - 1) {
      newSelected = newSelected.concat(selectedOrders.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedOrders.slice(0, selectedIndex),
        selectedOrders.slice(selectedIndex + 1)
      );
    }

    setSelectedOrders(newSelected);
  };

  const handleBulkAction = async () => {
    if (selectedOrders.length === 0) {
      showNotification('Please select orders first', 'error');
      return;
    }

    if (!bulkAction) {
      showNotification('Please select an action', 'error');
      return;
    }

    try {
      await api.patch('/orders/bulk/update', {
        orderIds: selectedOrders,
        action: 'status',
        value: bulkAction
      });
      
      fetchOrders();
      setBulkAction('');
      showNotification(`Updated ${selectedOrders.length} orders successfully`, 'success');
    } catch (error) {
      console.error('Error performing bulk action:', error);
      showNotification('Failed to update orders', 'error');
    }
  };

  const showNotification = (message: string, severity: 'success' | 'error') => {
    setNotification({ open: true, message, severity });
  };

  const isSelected = (orderId: number) => selectedOrders.indexOf(orderId) !== -1;

  const getCollectionDates = (order: Order): string[] => {
    if (order.collection_dates) {
      try {
        const dates = JSON.parse(order.collection_dates);
        if (Array.isArray(dates) && dates.length > 0) {
          return dates;
        }
      } catch (e) {
        // If parsing fails, fall back to shipping_collection_date
      }
    }
    
    if (order.shipping_collection_date) {
      return [order.shipping_collection_date];
    }
    
    return [];
  };

  const getFirstCollectionDate = (order: Order): string => {
    const dates = getCollectionDates(order);
    if (dates.length > 0) {
      return new Date(dates[0]).toLocaleDateString();
    }
    return 'N/A';
  };

  const handleDatePopoverOpen = (event: React.MouseEvent<HTMLElement>, dates: string[]) => {
    setDatePopover({ anchorEl: event.currentTarget, dates });
  };

  const handleDatePopoverClose = () => {
    setDatePopover({ anchorEl: null, dates: [] });
  };

  const handleViewOrderDetails = async (orderId: number) => {
    setLoadingDetails(true);
    setOrderDetailsModal({ open: true, order: null });
    setCollectionEvents([]);
    
    try {
      const [orderResponse, eventsResponse] = await Promise.all([
        api.get(`/orders/${orderId}`),
        api.get(`/collection-events/order/${orderId}`)
      ]);
      setOrderDetailsModal({ open: true, order: orderResponse.data });
      setCollectionEvents(eventsResponse.data);
      console.log('Order loaded:', orderResponse.data);
      console.log('Collection events loaded:', eventsResponse.data);
      console.log('Order collection_status:', orderResponse.data.collection_status);
    } catch (error) {
      console.error('Error fetching order details:', error);
      showNotification('Failed to fetch order details', 'error');
      setOrderDetailsModal({ open: false, order: null });
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleOverrideComplete = async (eventId: number) => {
    try {
      await api.put(`/collection-events/${eventId}/override-complete`);
      showNotification('Collection marked as completed', 'success');
      // Refresh the collection events and order details
      if (orderDetailsModal.order) {
        const [orderResponse, eventsResponse] = await Promise.all([
          api.get(`/orders/${orderDetailsModal.order.id}`),
          api.get(`/collection-events/order/${orderDetailsModal.order.id}`)
        ]);
        setOrderDetailsModal({ open: true, order: orderResponse.data });
        setCollectionEvents(eventsResponse.data);
        // Refresh the main orders list
        fetchOrders();
      }
    } catch (error) {
      console.error('Error marking collection as complete:', error);
      showNotification('Failed to mark collection as complete', 'error');
    }
  };

  const handleOpenAddEventDialog = () => {
    setDateDialog({
      open: true,
      mode: 'add',
      eventId: null,
      currentDate: ''
    });
    setSelectedDate('');
  };

  const handleOpenEditDateDialog = (eventId: number, currentDate: string) => {
    setDateDialog({
      open: true,
      mode: 'edit',
      eventId,
      currentDate
    });
    setSelectedDate(currentDate);
  };

  const handleCloseDateDialog = () => {
    setDateDialog({
      open: false,
      mode: 'add',
      eventId: null,
      currentDate: ''
    });
    setSelectedDate('');
  };

  const handleSaveDate = async () => {
    if (!selectedDate) {
      showNotification('Please select a date', 'error');
      return;
    }

    if (!orderDetailsModal.order) {
      showNotification('No order selected', 'error');
      return;
    }

    try {
      if (dateDialog.mode === 'add') {
        // Create new collection event
        await api.post('/collection-events', {
          orderId: orderDetailsModal.order.id,
          scheduledDate: selectedDate
        });
        showNotification('Collection event added successfully', 'success');
      } else if (dateDialog.mode === 'edit' && dateDialog.eventId) {
        // Update existing collection event date
        await api.patch(`/collection-events/${dateDialog.eventId}/date`, {
          scheduledDate: selectedDate
        });
        showNotification('Collection event date updated successfully', 'success');
      }

      // Refresh the collection events
      const eventsResponse = await api.get(`/collection-events/order/${orderDetailsModal.order.id}`);
      setCollectionEvents(eventsResponse.data);

      // Close the dialog
      handleCloseDateDialog();

      // Refresh the main orders list
      fetchOrders();
    } catch (error: any) {
      console.error('Error saving collection event:', error);
      const errorMessage = error.response?.data?.message || 'Failed to save collection event';
      showNotification(errorMessage, 'error');
    }
  };

  const handleOverrideOrderComplete = async () => {
    if (!orderDetailsModal.order) return;
    
    try {
      await api.patch(`/orders/${orderDetailsModal.order.id}/override-complete`);
      showNotification('Order marked as completed', 'success');
      // Refresh the order details and collection events
      const [orderResponse, eventsResponse] = await Promise.all([
        api.get(`/orders/${orderDetailsModal.order.id}`),
        api.get(`/collection-events/order/${orderDetailsModal.order.id}`)
      ]);
      setOrderDetailsModal({ open: true, order: orderResponse.data });
      setCollectionEvents(eventsResponse.data);
      // Refresh the main orders list
      fetchOrders();
    } catch (error) {
      console.error('Error marking order as complete:', error);
      showNotification('Failed to mark order as complete', 'error');
    }
  };

  const handleCloseOrderDetails = () => {
    setOrderDetailsModal({ open: false, order: null });
  };

  const handleRevertOrderComplete = async () => {
    if (!revertConfirmDialog.orderId) return;
    
    try {
      await api.patch(`/orders/${revertConfirmDialog.orderId}/revert-complete`);
      showNotification('Order reverted to assigned status', 'success');
      
      // Refresh the order details and collection events
      const [orderResponse, eventsResponse] = await Promise.all([
        api.get(`/orders/${revertConfirmDialog.orderId}`),
        api.get(`/collection-events/order/${revertConfirmDialog.orderId}`)
      ]);
      setOrderDetailsModal({ open: true, order: orderResponse.data });
      setCollectionEvents(eventsResponse.data);
      
      // Refresh the main orders list
      fetchOrders();
      
      // Close the confirmation dialog
      setRevertConfirmDialog({ open: false, orderId: null });
    } catch (error) {
      console.error('Error reverting order:', error);
      showNotification('Failed to revert order status', 'error');
      setRevertConfirmDialog({ open: false, orderId: null });
    }
  };

  const handleCancelOrder = async () => {
    if (!cancelConfirmDialog.orderId) return;
    
    try {
      await api.post(`/orders/${cancelConfirmDialog.orderId}/cancel`);
      showNotification('Order and all collection events have been cancelled', 'success');
      
      // Close confirmation dialog
      setCancelConfirmDialog({ open: false, orderId: null });
      
      // Refresh the order details if modal is open
      if (orderDetailsModal.order && orderDetailsModal.order.id === cancelConfirmDialog.orderId) {
        const [orderResponse, eventsResponse] = await Promise.all([
          api.get(`/orders/${cancelConfirmDialog.orderId}`),
          api.get(`/collection-events/order/${cancelConfirmDialog.orderId}`)
        ]);
        setOrderDetailsModal({ open: true, order: orderResponse.data });
        setCollectionEvents(eventsResponse.data);
      }
      
      // Refresh the main orders list
      fetchOrders();
    } catch (error) {
      console.error('Error cancelling order:', error);
      showNotification('Failed to cancel order', 'error');
    }
  };

  // Address editing handlers
  const handleStartEditAddress = () => {
    if (orderDetailsModal.order) {
      setEditAddressData({
        delivery_address_1: orderDetailsModal.order.delivery_address_1 || '',
        delivery_address_2: orderDetailsModal.order.delivery_address_2 || '',
        delivery_city: orderDetailsModal.order.delivery_city || '',
        delivery_postcode: orderDetailsModal.order.delivery_postcode || '',
        delivery_country: orderDetailsModal.order.delivery_country || ''
      });
      setIsEditingAddress(true);
    }
  };

  const handleCancelEditAddress = () => {
    setIsEditingAddress(false);
  };

  const handleSaveAddress = async () => {
    if (!orderDetailsModal.order) return;

    setSavingAddress(true);
    try {
      await api.patch(`/orders/${orderDetailsModal.order.id}/address`, editAddressData);
      showNotification('Address updated successfully', 'success');

      // Refresh order details
      const [orderResponse, eventsResponse] = await Promise.all([
        api.get(`/orders/${orderDetailsModal.order.id}`),
        api.get(`/collection-events/order/${orderDetailsModal.order.id}`)
      ]);
      setOrderDetailsModal({ open: true, order: orderResponse.data });
      setCollectionEvents(eventsResponse.data);
      setIsEditingAddress(false);

      // Refresh main list
      fetchOrders();
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to update address';
      showNotification(message, 'error');
    } finally {
      setSavingAddress(false);
    }
  };

  // Admin note append handler
  const handleSaveAdminNote = async () => {
    if (!orderDetailsModal.order || !adminNoteText.trim()) return;

    setSavingNote(true);
    try {
      // Append admin note to existing customer_note
      const existingNote = orderDetailsModal.order.customer_note || '';
      const separator = existingNote ? '\n--- Admin Note ---\n' : '';
      const newNote = existingNote + separator + adminNoteText.trim();

      await api.patch(`/orders/${orderDetailsModal.order.id}/customer-note`, {
        customer_note: newNote
      });
      showNotification('Note added successfully', 'success');

      // Refresh order details
      const orderResponse = await api.get(`/orders/${orderDetailsModal.order.id}`);
      setOrderDetailsModal({ open: true, order: orderResponse.data });
      setAdminNoteText('');
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to add note';
      showNotification(message, 'error');
    } finally {
      setSavingNote(false);
    }
  };

  // Edit full delivery note handler
  const handleStartEditNote = () => {
    if (orderDetailsModal.order) {
      setEditNoteText(orderDetailsModal.order.customer_note || '');
      setIsEditingNote(true);
    }
  };

  const handleCancelEditNote = () => {
    setIsEditingNote(false);
  };

  const handleSaveEditedNote = async () => {
    if (!orderDetailsModal.order) return;

    setSavingNote(true);
    try {
      await api.patch(`/orders/${orderDetailsModal.order.id}/customer-note`, {
        customer_note: editNoteText
      });
      showNotification('Delivery notes updated successfully', 'success');

      // Refresh order details
      const orderResponse = await api.get(`/orders/${orderDetailsModal.order.id}`);
      setOrderDetailsModal({ open: true, order: orderResponse.data });
      setIsEditingNote(false);
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to update notes';
      showNotification(message, 'error');
    } finally {
      setSavingNote(false);
    }
  };

  // Bin colours editing handlers
  const handleStartEditBinColours = () => {
    if (orderDetailsModal.order) {
      setEditBinColours({
        bin_brown: orderDetailsModal.order.bin_brown || 0,
        bin_green: orderDetailsModal.order.bin_green || 0,
        bin_blue: orderDetailsModal.order.bin_blue || 0,
        bin_black: orderDetailsModal.order.bin_black || 0,
        bin_grey: orderDetailsModal.order.bin_grey || 0,
        bin_pink: orderDetailsModal.order.bin_pink || 0,
      });
      setIsEditingBinColours(true);
    }
  };

  const handleCancelEditBinColours = () => {
    setIsEditingBinColours(false);
  };

  const handleSaveBinColours = async () => {
    if (!orderDetailsModal.order) return;

    setSavingBinColours(true);
    try {
      await api.patch(`/orders/${orderDetailsModal.order.id}/bin-colours`, editBinColours);
      showNotification('Bin colours updated successfully', 'success');

      // Refresh order details
      const [orderResponse, eventsResponse] = await Promise.all([
        api.get(`/orders/${orderDetailsModal.order.id}`),
        api.get(`/collection-events/order/${orderDetailsModal.order.id}`)
      ]);
      setOrderDetailsModal({ open: true, order: orderResponse.data });
      setCollectionEvents(eventsResponse.data);
      setIsEditingBinColours(false);

      // Refresh main list
      fetchOrders();
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to update bin colours';
      showNotification(message, 'error');
    } finally {
      setSavingBinColours(false);
    }
  };

  const getBinColours = (order: Order): string[] => {
    const colours: string[] = [];
    
    // Check individual bin colour counts
    if (order.bin_brown && order.bin_brown > 0) colours.push(`${order.bin_brown} Brown`);
    if (order.bin_green && order.bin_green > 0) colours.push(`${order.bin_green} Green`);
    if (order.bin_blue && order.bin_blue > 0) colours.push(`${order.bin_blue} Blue`);
    if (order.bin_black && order.bin_black > 0) colours.push(`${order.bin_black} Black`);
    if (order.bin_grey && order.bin_grey > 0) colours.push(`${order.bin_grey} Grey`);
    if (order.bin_pink && order.bin_pink > 0) colours.push(`${order.bin_pink} Pink`);
    
    // If no individual counts but bin_colours field exists, try to parse it
    if (colours.length === 0 && order.bin_colours) {
      try {
        const parsedColours = JSON.parse(order.bin_colours);
        if (Array.isArray(parsedColours)) {
          colours.push(...parsedColours);
        }
      } catch (e) {
        // If not JSON, use as is
        colours.push(order.bin_colours);
      }
    }

    // Append the free / one-off bin colour(s), which live on the first date only
    if (order.first_date_bin_colours) {
      try {
        const fd = JSON.parse(order.first_date_bin_colours);
        if (Array.isArray(fd)) colours.push(...fd);
      } catch (e) { /* ignore */ }
    }

    return colours;
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Order Management
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <TextField
            label="Search"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Order #, Name, Postcode"
            sx={{ minWidth: 250 }}
          />
          

          <Button 
            variant="contained" 
            sx={{ backgroundColor: '#23a244', '&:hover': { backgroundColor: '#1e8e3a' } }}
            onClick={fetchOrders}
          >
            Refresh
          </Button>

          {selectedOrders.length > 0 && (
            <>
              <Typography variant="body2" sx={{ ml: 2 }}>
                {selectedOrders.length} selected
              </Typography>
              
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Bulk Action</InputLabel>
                <Select
                  value={bulkAction}
                  label="Bulk Action"
                  onChange={(e) => setBulkAction(e.target.value)}
                >
                  <MenuItem value="">Select Action</MenuItem>
                  <MenuItem value="pending">Set as Pending</MenuItem>
                  <MenuItem value="processing">Set as Processing</MenuItem>
                  <MenuItem value="completed">Set as Completed</MenuItem>
                  <MenuItem value="cancelled">Set as Cancelled</MenuItem>
                </Select>
              </FormControl>

              <Button 
                variant="contained" 
                color="primary"
                onClick={handleBulkAction}
                disabled={!bulkAction}
              >
                Apply to Selected
              </Button>
            </>
          )}
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedOrders.length > 0 && selectedOrders.length < orders.length}
                  checked={orders.length > 0 && selectedOrders.length === orders.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell sx={{ minWidth: 86 }}>Order #</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Postcode</TableCell>
              <TableCell>Products</TableCell>
              <TableCell>Collection Dates</TableCell>
              <TableCell>Collection Status</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No orders found
                </TableCell>
              </TableRow>
            ) : (
              orders.map((order) => {
                const isItemSelected = isSelected(order.id);
                
                return (
                  <TableRow 
                    key={order.id}
                    selected={isItemSelected}
                    hover
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isItemSelected}
                        onChange={() => handleSelectOrder(order.id)}
                      />
                    </TableCell>
                    <TableCell>{order.order_number}</TableCell>
                    <TableCell>
                      {order.customer_first_name} {order.customer_last_name}
                      {(order.loyalty_member ?? 0) > 0 && (order.previous_orders_count ?? 0) > 0 && (
                        <Tooltip title="These are customers with accounts and are part of the loyalty scheme">
                          <Box
                            component="span"
                            sx={{ ml: 0.5, color: 'primary.main', fontWeight: 700, fontSize: '1.1rem', lineHeight: 1, cursor: 'help' }}
                          >
                            *
                          </Box>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>{order.delivery_postcode}</TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>
                      <Typography variant="body2" noWrap title={order.product_names || 'N/A'}>
                        {order.product_names || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <Typography variant="body2">
                          {getFirstCollectionDate(order)}
                        </Typography>
                        {(() => {
                          const dates = getCollectionDates(order);
                          if (dates.length > 1) {
                            return (
                              <IconButton 
                                size="small" 
                                onClick={(e) => handleDatePopoverOpen(e, dates)}
                                sx={{ padding: '2px' }}
                                title={`View all ${dates.length} collection dates`}
                              >
                                <AddCircleOutline fontSize="small" sx={{ fontSize: 18 }} />
                                <Typography variant="caption" sx={{ ml: 0.5 }}>
                                  +{dates.length - 1}
                                </Typography>
                              </IconButton>
                            );
                          }
                          return null;
                        })()}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {(() => {
                        // Check if order is cancelled
                        if (order.status === 'cancelled' || order.collection_status === 'cancelled') {
                          return <Chip label="CANCELLED" color="error" size="small" />;
                        }
                        
                        // Check order collection_status first for consistency
                        if (order.collection_status === 'collection_complete') {
                          return <Chip label="COMPLETED" color="success" size="small" />;
                        }
                        
                        // Use real collection_events data
                        const total = order.total_collections || 0;
                        const completed = order.completed_collections || 0;
                        const pending = order.pending_collections || 0;
                        
                        if (total === 0) {
                          return <Chip label="NO COLLECTIONS" color="default" size="small" />;
                        }
                        
                        if (completed === total) {
                          return <Chip label="COMPLETED" color="success" size="small" />;
                        }
                        
                        if (pending > 0) {
                          return (
                            <Chip 
                              label={`${pending} COLLECTION${pending > 1 ? 'S' : ''} LEFT`} 
                              color="warning" 
                              size="small" 
                            />
                          );
                        }
                        
                        // All are in past but not completed
                        return <Chip label="OVERDUE" color="error" size="small" />;
                      })()}
                    </TableCell>
                    <TableCell>£{parseFloat(order.total).toFixed(2)}</TableCell>
                    <TableCell>
                      <IconButton 
                        size="small" 
                        title="View Details"
                        onClick={() => handleViewOrderDetails(order.id)}
                      >
                        <Visibility />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 20, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert 
          onClose={() => setNotification({ ...notification, open: false })} 
          severity={notification.severity}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      <Popover
        open={Boolean(datePopover.anchorEl)}
        anchorEl={datePopover.anchorEl}
        onClose={handleDatePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, minWidth: 200 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            All Collection Dates
          </Typography>
          <List dense>
            {datePopover.dates.map((date, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemText 
                  primary={`${index + 1}. ${new Date(date).toLocaleDateString()}`}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Popover>

      {/* Order Details Modal */}
      <Dialog 
        open={orderDetailsModal.open} 
        onClose={handleCloseOrderDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Order Details #{orderDetailsModal.order?.order_number}
            </Typography>
            <IconButton onClick={handleCloseOrderDetails} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {loadingDetails ? (
            <Box display="flex" justifyContent="center" py={4}>
              <Typography>Loading order details...</Typography>
            </Box>
          ) : orderDetailsModal.order ? (
            <Grid container spacing={3}>
              {/* Customer Information */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Customer Information
                </Typography>
                <Box mb={2}>
                  <Typography variant="body1" fontWeight="medium">
                    {orderDetailsModal.order.customer_first_name} {orderDetailsModal.order.customer_last_name}
                  </Typography>
                  {orderDetailsModal.order.customer_email && (
                    <Box display="flex" alignItems="center" gap={1} mt={1}>
                      <Email fontSize="small" color="action" />
                      <Typography variant="body2">{orderDetailsModal.order.customer_email}</Typography>
                    </Box>
                  )}
                  {orderDetailsModal.order.customer_phone && (
                    <Box display="flex" alignItems="center" gap={1} mt={1}>
                      <Phone fontSize="small" color="action" />
                      <Typography variant="body2">{orderDetailsModal.order.customer_phone}</Typography>
                    </Box>
                  )}
                </Box>
              </Grid>

              {/* Collection Address */}
              <Grid item xs={12} md={6}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Collection Address
                  </Typography>
                  {!isEditingAddress && orderDetailsModal.order.status !== 'cancelled' && (
                    <IconButton size="small" onClick={handleStartEditAddress} title="Edit Address">
                      <Edit fontSize="small" />
                    </IconButton>
                  )}
                </Box>

                {isEditingAddress ? (
                  <Box>
                    <TextField
                      fullWidth
                      size="small"
                      label="Address Line 1"
                      value={editAddressData.delivery_address_1}
                      onChange={(e) => setEditAddressData({...editAddressData, delivery_address_1: e.target.value})}
                      sx={{ mb: 1 }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Address Line 2"
                      value={editAddressData.delivery_address_2}
                      onChange={(e) => setEditAddressData({...editAddressData, delivery_address_2: e.target.value})}
                      sx={{ mb: 1 }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="City"
                      value={editAddressData.delivery_city}
                      onChange={(e) => setEditAddressData({...editAddressData, delivery_city: e.target.value})}
                      sx={{ mb: 1 }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Postcode"
                      value={editAddressData.delivery_postcode}
                      onChange={(e) => setEditAddressData({...editAddressData, delivery_postcode: e.target.value})}
                      sx={{ mb: 1 }}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      label="Country"
                      value={editAddressData.delivery_country}
                      onChange={(e) => setEditAddressData({...editAddressData, delivery_country: e.target.value})}
                      sx={{ mb: 1 }}
                    />
                    <Box display="flex" gap={1} mt={1}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleSaveAddress}
                        disabled={savingAddress}
                      >
                        {savingAddress ? 'Saving...' : 'Save'}
                      </Button>
                      <Button size="small" variant="outlined" onClick={handleCancelEditAddress}>
                        Cancel
                      </Button>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Changes will apply to all pending collections for this order.
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      Note: Map location only updates if the postcode is changed.
                    </Typography>
                  </Box>
                ) : (
                  <Box display="flex" alignItems="flex-start" gap={1}>
                    <LocationOn fontSize="small" color="action" sx={{ mt: 0.5 }} />
                    <Box>
                      <Typography variant="body2">{orderDetailsModal.order.delivery_address_1}</Typography>
                      {orderDetailsModal.order.delivery_address_2 && (
                        <Typography variant="body2">{orderDetailsModal.order.delivery_address_2}</Typography>
                      )}
                      <Typography variant="body2">
                        {orderDetailsModal.order.delivery_city}, {orderDetailsModal.order.delivery_postcode}
                      </Typography>
                      {orderDetailsModal.order.delivery_country && (
                        <Typography variant="body2">{orderDetailsModal.order.delivery_country}</Typography>
                      )}
                    </Box>
                  </Box>
                )}
              </Grid>

              {/* Billing Address */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Billing Address
                </Typography>
                <Box display="flex" alignItems="flex-start" gap={1}>
                  <LocationOn fontSize="small" color="action" sx={{ mt: 0.5 }} />
                  <Box>
                    <Typography variant="body2">{orderDetailsModal.order.billing_address_1}</Typography>
                    {orderDetailsModal.order.billing_address_2 && (
                      <Typography variant="body2">{orderDetailsModal.order.billing_address_2}</Typography>
                    )}
                    <Typography variant="body2">
                      {orderDetailsModal.order.billing_city}, {orderDetailsModal.order.billing_postcode}
                    </Typography>
                    {orderDetailsModal.order.billing_country && (
                      <Typography variant="body2">{orderDetailsModal.order.billing_country}</Typography>
                    )}
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Divider />
              </Grid>

              {/* Order Information */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Order Information
                </Typography>
                <Box mb={1}>
                  <Typography variant="body2">
                    <strong>Order Date:</strong> {orderDetailsModal.order.date_created ? new Date(orderDetailsModal.order.date_created).toLocaleString() : 'N/A'}
                  </Typography>
                </Box>
                <Box mb={1}>
                  <Typography variant="body2">
                    <strong>Collection Status:</strong>{' '}
                    {(() => {
                      // Check if order is cancelled
                      if (orderDetailsModal.order.status === 'cancelled' || orderDetailsModal.order.collection_status === 'cancelled') {
                        return <Chip label="CANCELLED" color="error" size="small" />;
                      }
                      
                      // Use same logic as main table for consistency
                      if (orderDetailsModal.order.collection_status === 'collection_complete') {
                        return <Chip label="COMPLETED" color="success" size="small" />;
                      }
                      
                      const total = orderDetailsModal.order.total_collections || collectionEvents.length || 0;
                      const completed = orderDetailsModal.order.completed_collections || collectionEvents.filter(e => e.status === 'completed').length || 0;
                      const pending = orderDetailsModal.order.pending_collections || collectionEvents.filter(e => e.status !== 'completed' && e.status !== 'cancelled').length || 0;
                      
                      if (total === 0) {
                        return <Chip label="NO COLLECTIONS" color="default" size="small" />;
                      }
                      
                      if (completed === total) {
                        return <Chip label="COMPLETED" color="success" size="small" />;
                      }
                      
                      if (pending > 0) {
                        return (
                          <Chip 
                            label={`${pending} COLLECTION${pending > 1 ? 'S' : ''} LEFT`} 
                            color="warning" 
                            size="small" 
                          />
                        );
                      }
                      
                      return <Chip label="OVERDUE" color="error" size="small" />;
                    })()}
                  </Typography>
                </Box>
                <Box mb={1}>
                  <Typography variant="body2">
                    <strong>Total Amount:</strong> £{parseFloat(orderDetailsModal.order.total).toFixed(2)}
                  </Typography>
                </Box>
                {(() => {
                  // Get the current driver from the latest assigned collection event
                  const currentEvent = collectionEvents.find(e => 
                    e.status === 'assigned' || e.status === 'pending'
                  ) || collectionEvents[0];
                  const currentDriver = currentEvent?.driver_name || currentEvent?.current_driver_name;
                  
                  if (currentDriver) {
                    return (
                      <Box mb={1}>
                        <Typography variant="body2">
                          <strong>Current Driver:</strong> {currentDriver}
                        </Typography>
                        {currentEvent?.assigned_at && (
                          <Typography variant="body2">
                            <strong>Assigned At:</strong> {new Date(currentEvent.assigned_at).toLocaleString()}
                          </Typography>
                        )}
                      </Box>
                    );
                  }
                  return null;
                })()}
                {orderDetailsModal.order.completed_at && (
                  <Box mb={1}>
                    <Typography variant="body2">
                      <strong>Completed At:</strong> {new Date(orderDetailsModal.order.completed_at).toLocaleString()}
                    </Typography>
                  </Box>
                )}
              </Grid>

              {/* Collection Events */}
              <Grid item xs={12}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Collection Events
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    startIcon={<Add />}
                    onClick={handleOpenAddEventDialog}
                  >
                    Add Event
                  </Button>
                </Box>
                {collectionEvents.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Driver</TableCell>
                          <TableCell>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {collectionEvents.map((event) => (
                          <TableRow key={event.id}>
                            <TableCell>
                              {new Date(event.scheduled_date).toLocaleDateString()}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={event.status.toUpperCase()}
                                size="small"
                                color={
                                  event.status === 'completed' ? 'success' :
                                  event.status === 'assigned' ? 'info' :
                                  event.status === 'pending' ? 'warning' : 'default'
                                }
                              />
                              {event.override_completed === 1 && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  (Override)
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {event.current_driver_name || event.driver_name || '-'}
                            </TableCell>
                            <TableCell>
                              {event.status !== 'completed' ? (
                                <Box display="flex" gap={1}>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleOpenEditDateDialog(event.id, event.scheduled_date)}
                                    title="Edit Date"
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="primary"
                                    onClick={() => handleOverrideComplete(event.id)}
                                  >
                                    Override Complete
                                  </Button>
                                </Box>
                              ) : (
                                <Box display="flex" alignItems="center" gap={0.5}>
                                  <Lock fontSize="small" color="disabled" />
                                  <Typography variant="body2" color="text.secondary">
                                    Completed
                                  </Typography>
                                </Box>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No collection events found
                  </Typography>
                )}
              </Grid>

              {/* Products Ordered */}
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Products Ordered
                </Typography>
                <Typography variant="body2">
                  {orderDetailsModal.order.product_names || 'No product information available'}
                </Typography>

                {((orderDetailsModal.order.beds_count ?? 0) > 0 || (orderDetailsModal.order.mattress_count ?? 0) > 0) && (
                  <Box mt={1}>
                    {(orderDetailsModal.order.beds_count ?? 0) > 0 && (
                      <Typography variant="body2">
                        <strong>Beds:</strong> {orderDetailsModal.order.beds_count}
                      </Typography>
                    )}
                    {(orderDetailsModal.order.mattress_count ?? 0) > 0 && (
                      <Typography variant="body2">
                        <strong>Mattresses:</strong> {orderDetailsModal.order.mattress_count}
                      </Typography>
                    )}
                  </Box>
                )}


                {/* Service Details */}
                {(((orderDetailsModal.order.bins_per_occasion ?? 0) > 0) ||
                  ((orderDetailsModal.order.bags_per_occasion ?? 0) > 0) ||
                  ((orderDetailsModal.order.large_bins_per_occasion ?? 0) > 0) ||
                  ((orderDetailsModal.order.bins_first_date ?? 0) > 0) ||
                  ((orderDetailsModal.order.bags_first_date ?? 0) > 0) ||
                  ((orderDetailsModal.order.large_bins_first_date ?? 0) > 0)) && (
                  <Box mt={2}>
                    {(orderDetailsModal.order.bins_per_occasion ?? 0) > 0 && (
                      <Typography variant="body2">
                        <strong>Bins:</strong> {orderDetailsModal.order.bins_per_occasion} per occasion × {orderDetailsModal.order.bins_number_of_occasions || 1} {(orderDetailsModal.order.bins_number_of_occasions || 1) === 1 ? 'occasion' : 'occasions'}
                      </Typography>
                    )}
                    {(orderDetailsModal.order.bags_per_occasion ?? 0) > 0 && (
                      <Typography variant="body2">
                        <strong>Bags:</strong> {orderDetailsModal.order.bags_per_occasion} per occasion × {orderDetailsModal.order.bags_number_of_occasions || 1} {(orderDetailsModal.order.bags_number_of_occasions || 1) === 1 ? 'occasion' : 'occasions'}
                      </Typography>
                    )}
                    {(orderDetailsModal.order.large_bins_per_occasion ?? 0) > 0 && (
                      <Typography variant="body2">
                        <strong>Large Bins:</strong> {orderDetailsModal.order.large_bins_per_occasion} per occasion × {orderDetailsModal.order.large_bins_number_of_occasions || 1} {(orderDetailsModal.order.large_bins_number_of_occasions || 1) === 1 ? 'occasion' : 'occasions'}
                      </Typography>
                    )}
                    {(orderDetailsModal.order.bins_first_date ?? 0) > 0 && (
                      <Typography variant="body2">
                        <strong>Free bin (first collection date only):</strong> {orderDetailsModal.order.bins_first_date}
                      </Typography>
                    )}
                    {(orderDetailsModal.order.bags_first_date ?? 0) > 0 && (
                      <Typography variant="body2">
                        <strong>Free bags (first collection date only):</strong> {orderDetailsModal.order.bags_first_date}
                      </Typography>
                    )}
                    {(orderDetailsModal.order.large_bins_first_date ?? 0) > 0 && (
                      <Typography variant="body2">
                        <strong>Free large bin (first collection date only):</strong> {orderDetailsModal.order.large_bins_first_date}
                      </Typography>
                    )}
                  </Box>
                )}
                
                {/* Bin Colours — only shown for bin orders */}
                {((orderDetailsModal.order.bins_per_occasion ?? 0) > 0 || (orderDetailsModal.order.bins_first_date ?? 0) > 0 || getBinColours(orderDetailsModal.order).length > 0) && (
                <Box mt={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2">
                      <strong>Bin Colours:</strong>{' '}
                      {!isEditingBinColours && (getBinColours(orderDetailsModal.order).join(', ') || 'None set')}
                    </Typography>
                    {!isEditingBinColours && orderDetailsModal.order.status !== 'cancelled' && (
                      <IconButton size="small" onClick={handleStartEditBinColours} title="Edit Bin Colours">
                        <Edit fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  {isEditingBinColours && (
                    <Box mt={1}>
                      <Grid container spacing={1}>
                        {[
                          { key: 'bin_brown', label: 'Brown' },
                          { key: 'bin_green', label: 'Green' },
                          { key: 'bin_blue', label: 'Blue' },
                          { key: 'bin_black', label: 'Black' },
                          { key: 'bin_grey', label: 'Grey' },
                          { key: 'bin_pink', label: 'Pink' },
                        ].map(({ key, label }) => (
                          <Grid item xs={4} key={key}>
                            <TextField
                              label={label}
                              type="number"
                              size="small"
                              fullWidth
                              value={editBinColours[key as keyof typeof editBinColours]}
                              onChange={(e) => setEditBinColours({
                                ...editBinColours,
                                [key]: parseInt(e.target.value) || 0
                              })}
                              inputProps={{ min: 0 }}
                            />
                          </Grid>
                        ))}
                      </Grid>
                      <Box display="flex" gap={1} mt={1}>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={handleSaveBinColours}
                          disabled={savingBinColours}
                        >
                          {savingBinColours ? 'Saving...' : 'Save'}
                        </Button>
                        <Button size="small" variant="outlined" onClick={handleCancelEditBinColours}>
                          Cancel
                        </Button>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Changes will apply to all pending collections for this order.
                      </Typography>
                    </Box>
                  )}
                </Box>
                )}
              </Grid>

              {/* Customer Notes */}
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Delivery Notes
                  </Typography>
                  {!isEditingNote && orderDetailsModal.order.status !== 'cancelled' && (
                    <IconButton size="small" onClick={handleStartEditNote} title="Edit Notes">
                      <Edit fontSize="small" />
                    </IconButton>
                  )}
                </Box>

                {isEditingNote ? (
                  <Box>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      size="small"
                      value={editNoteText}
                      onChange={(e) => setEditNoteText(e.target.value)}
                      placeholder="Enter delivery notes..."
                      sx={{ mb: 1 }}
                    />
                    <Box display="flex" gap={1}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={handleSaveEditedNote}
                        disabled={savingNote}
                      >
                        {savingNote ? 'Saving...' : 'Save'}
                      </Button>
                      <Button size="small" variant="outlined" onClick={handleCancelEditNote}>
                        Cancel
                      </Button>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Changes will apply to all pending collections for this order.
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mb: 2 }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {orderDetailsModal.order.customer_note || 'No delivery notes'}
                      </Typography>
                    </Paper>

                    {/* Add Admin Note */}
                    {orderDetailsModal.order.status !== 'cancelled' && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Add Admin Note
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          rows={2}
                          size="small"
                          value={adminNoteText}
                          onChange={(e) => setAdminNoteText(e.target.value)}
                          placeholder="Add a note for the driver..."
                          sx={{ mb: 1 }}
                        />
                        <Box display="flex" gap={1}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={handleSaveAdminNote}
                            disabled={savingNote || !adminNoteText.trim()}
                          >
                            {savingNote ? 'Saving...' : 'Add Note'}
                          </Button>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                          Note will be appended and shown to drivers on all pending collections.
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </Grid>
            </Grid>
          ) : (
            <Typography>No order details available</Typography>
          )}
        </DialogContent>
        <DialogActions>
          {orderDetailsModal.order && orderDetailsModal.order.status !== 'cancelled' && orderDetailsModal.order.collection_status !== 'collection_complete' && (
            <>
              <Button 
                onClick={() => setCancelConfirmDialog({ open: true, orderId: orderDetailsModal.order!.id })}
                color="error"
                variant="outlined"
                sx={{ mr: 'auto' }}
              >
                Cancel Order
              </Button>
              <Button 
                onClick={handleOverrideOrderComplete}
                color="primary"
                variant="contained"
                sx={{ mr: 1 }}
              >
                Override Order Complete
              </Button>
            </>
          )}
          {orderDetailsModal.order && orderDetailsModal.order.status !== 'cancelled' && orderDetailsModal.order.collection_status === 'collection_complete' && (
            <Button 
              onClick={() => setRevertConfirmDialog({ open: true, orderId: orderDetailsModal.order!.id })}
              color="warning"
              variant="outlined"
              sx={{ mr: 'auto' }}
            >
              Revert to Assigned
            </Button>
          )}
          <Button onClick={handleCloseOrderDetails}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Order Confirmation Dialog */}
      <Dialog
        open={cancelConfirmDialog.open}
        onClose={() => setCancelConfirmDialog({ open: false, orderId: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" color="error">
            Confirm Order Cancellation
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to cancel this order?
          </Alert>
          <Typography variant="body2" paragraph>
            This action will:
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <Typography component="li" variant="body2">Cancel all pending collection events</Typography>
            <Typography component="li" variant="body2">Remove them from today's collections</Typography>
            <Typography component="li" variant="body2">Remove them from upcoming collections</Typography>
            <Typography component="li" variant="body2" color="error" fontWeight="bold">This cannot be undone</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelConfirmDialog({ open: false, orderId: null })} color="primary">
            Cancel
          </Button>
          <Button onClick={handleCancelOrder} color="error" variant="contained">
            Yes, Cancel Order
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revert Order Completion Confirmation Dialog */}
      <Dialog
        open={revertConfirmDialog.open}
        onClose={() => setRevertConfirmDialog({ open: false, orderId: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" color="warning.main">
            Confirm Order Revert
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to revert this completed order back to assigned status?
          </Alert>
          <Typography variant="body2" paragraph>
            This action will:
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <Typography component="li" variant="body2">Change order status from "Complete" to "Assigned"</Typography>
            <Typography component="li" variant="body2">Reset collection events to "Assigned" status</Typography>
            <Typography component="li" variant="body2">Clear all completion timestamps and data</Typography>
            <Typography component="li" variant="body2">Remove completion overrides</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevertConfirmDialog({ open: false, orderId: null })} color="primary">
            Cancel
          </Button>
          <Button onClick={handleRevertOrderComplete} color="warning" variant="contained">
            Yes, Revert Order
          </Button>
        </DialogActions>
      </Dialog>

      {/* Date Picker Dialog for Add/Edit Collection Event */}
      <Dialog
        open={dateDialog.open}
        onClose={handleCloseDateDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {dateDialog.mode === 'add' ? 'Add Collection Event' : 'Edit Collection Date'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              label="Collection Date"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                min: new Date().toISOString().split('T')[0] // Prevent selecting past dates for new events
              }}
            />
            {dateDialog.mode === 'edit' && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Current date: {new Date(dateDialog.currentDate).toLocaleDateString()}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDateDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleSaveDate} color="primary" variant="contained">
            {dateDialog.mode === 'add' ? 'Add Event' : 'Update Date'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrderManagement;